import { Fragment, useContext } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

/* Helpers */
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

/* Components */
import { SbSharedProductAdWrapper } from '../SbSharedProductAdWrapper/SbSharedProductAdWrapper';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { SbSharedItemTrackingProvider } from '../SbSharedItemTrackingProvider/SbSharedItemTrackingProvider';
import { useMeasurementUnit } from '../useMeasurementUnit';
import { SbRespPlaceholderTile } from './SbRespPlaceholderTile';

import styles from './sbRespSearchResultContainer.scss';

import { useSearchCorrections } from '../useSearchCorrections';

/* Constants */
import { VERTICAL_OFFSET_SB_RESP_IMG } from '../../constants/imageConstants';

const ITEMS_PER_ROW = 3;
const LAZY_LOAD_ITEM_TILE_INDEX = ITEMS_PER_ROW * 2;
const ITEMS_PER_ROW_FOR_SB_TOP_NAV = 3;
const LAZY_LOAD_ITEM_TILE_INDEX_FOR_SB_TOP_NAV = ITEMS_PER_ROW_FOR_SB_TOP_NAV * 2;

const getItemsOrPlaceholders = ({ items, refetchInFlight, pageSize }) => {
    if (refetchInFlight) {
        const placeholderItem = { node: { item: {} } };
        return Array(pageSize).fill(placeholderItem);
    }
    return items || [];
};

const SbRespSearchResultContainerComponent = props => {
    const {
        currency,
        showMeasurements,
        showInternalSellerName,
        showSellerName,
        showCreatorName,
        showStorefrontOnlyBadge,
        itemSearch,
        viewer,
        relay,
        isClient,
        user,
    } = props;

    const { edges: items = [] } = itemSearch || {};

    const userId = user ? user.serviceId : null;

    const { isSbTopNavVariant } = useContext(ServerVarsContext);

    const [searchCorrectionTerm] = useSearchCorrections({ itemSearch });
    const [measurementUnit] = useMeasurementUnit({ itemSearch, user });
    /* eslint-disable react/prop-types */

    const pageSize = useSelector(state => state.relayVariables.variables?.first);
    const refetchInFlight = useSelector(state => state.filters.refetchInFlight);
    const itemsOrPlaceholders = getItemsOrPlaceholders({ items, refetchInFlight, pageSize });

    return (
        <div className={styles.container} data-tn="search-results-container">
            <FavoritesProvider
                disable={!isClient}
                environment={relay.environment}
                itemIds={
                    items ? items.filter(Boolean).map(({ node }) => node?.item?.serviceId) : []
                }
                location={locations.SEARCH_BROWSE}
                userId={userId}
            >
                {({ props: favorites }) => (
                    <QuickViewProvider>
                        {({ quickView }) => (
                            <SbSharedItemTrackingProvider
                                itemSearch={itemSearch}
                                searchCorrectionTerm={searchCorrectionTerm}
                                viewer={viewer}
                            >
                                {({ fireItemImpressionTracking, fireItemClickTracking }) =>
                                    itemsOrPlaceholders.map(({ node: { item } }, index) => {
                                        if (refetchInFlight) {
                                            return (
                                                <SbRespPlaceholderTile
                                                    key={item?.serviceId || index}
                                                />
                                            );
                                        }
                                        return (
                                            <Fragment key={item?.serviceId || index}>
                                                <SbSharedProductAdWrapper
                                                    index={index}
                                                    itemSearch={itemSearch}
                                                />

                                                <SharedItemTile
                                                    isVisuallySimilarEnabled
                                                    item={item}
                                                    user={user}
                                                    favorites={favorites}
                                                    index={index}
                                                    currency={currency}
                                                    isMobile={false}
                                                    showInternalSellerName={showInternalSellerName}
                                                    showStorefrontOnlyBadge={
                                                        showStorefrontOnlyBadge
                                                    }
                                                    showInternal
                                                    isClient={isClient}
                                                    showSellerName={showSellerName}
                                                    showCreatorName={showCreatorName}
                                                    showMeasurements={showMeasurements}
                                                    quickViewProps={quickView}
                                                    lazyLoadStartIndex={
                                                        isSbTopNavVariant
                                                            ? LAZY_LOAD_ITEM_TILE_INDEX_FOR_SB_TOP_NAV
                                                            : LAZY_LOAD_ITEM_TILE_INDEX
                                                    }
                                                    imageLoadVerticalOffset={
                                                        VERTICAL_OFFSET_SB_RESP_IMG
                                                    }
                                                    onContentVisible={() =>
                                                        fireItemImpressionTracking(index)
                                                    }
                                                    onClick={() => fireItemClickTracking(index)}
                                                    measurementUnit={measurementUnit}
                                                    viewer={viewer}
                                                    itemSearch={itemSearch}
                                                    fetchFolderOnMouseOver
                                                    showFavoritesTooltip={index === 0}
                                                />
                                            </Fragment>
                                        );
                                    })
                                }
                            </SbSharedItemTrackingProvider>
                        )}
                    </QuickViewProvider>
                )}
            </FavoritesProvider>
        </div>
    );
    /* eslint-enable react/prop-types */
};

SbRespSearchResultContainerComponent.propTypes = {
    currency: PropTypes.string.isRequired,
    showMeasurements: PropTypes.bool.isRequired,
    showInternalSellerName: PropTypes.bool,
    showSellerName: PropTypes.bool,
    showCreatorName: PropTypes.bool,
    showStorefrontOnlyBadge: PropTypes.bool,
    itemSearch: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    isClient: PropTypes.bool,
    user: PropTypes.object,
};

export const SbRespSearchResultContainer = createFragmentContainer(
    SbRespSearchResultContainerComponent,
    {
        viewer: graphql`
            fragment SbRespSearchResultContainer_viewer on Viewer {
                isSbTradeFreeShippingTestDisabled: isEnabled(key: "disableSbTradeFreeShippingTest")
                    @include(if: $isTrade)
                ...SharedItemTile_viewer
                ...SbSharedItemTrackingProvider_viewer
            }
        `,
        user: graphql`
            fragment SbRespSearchResultContainer_user on User {
                serviceId
                ...useMeasurementUnit_user
                ...SharedItemTile_user
            }
        `,
        itemSearch: graphql`
            fragment SbRespSearchResultContainer_itemSearch on ItemSearchQueryConnection {
                ...useMeasurementUnit_itemSearch
                ...SbSharedItemTrackingProvider_itemSearch
                ...useSearchCorrections_itemSearch
                ...SbSharedProductAdWrapper_itemSearch
                ...SharedItemTile_itemSearch
                edges {
                    node {
                        item {
                            serviceId
                            ...SharedItemTile_item @arguments(fetchVideo: $fetchTileVideo)
                        }
                    }
                }
            }
        `,
    }
);
